import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import type { Node as ProseMirrorNode } from '@milkdown/kit/prose/model';
import { $node, $remark, $command, $inputRule } from '@milkdown/kit/utils';
import { wrapIn } from '@milkdown/kit/prose/commands';
import { wrappingInputRule } from '@milkdown/kit/prose/inputrules';
import directive from 'remark-directive';

import { NOTICE_NODE_NAME } from './constants';

export type NoticeStyle = 'info' | 'warning' | 'tip';

export const NOTICE_STYLES: NoticeStyle[] = ['info', 'warning', 'tip'];

export const NOTICE_LABELS: Record<NoticeStyle, string> = {
  info: 'Info',
  warning: 'Warning',
  tip: 'Tip',
};

/**
 * Remark plugin to enable directive syntax (:::)
 */
export const remarkDirectivePlugin = $remark('notice-directive', () => directive);

/**
 * Notice node schema for container callouts
 */
export const noticeNode = $node('container_notice', () => ({
  group: 'block',
  content: 'block+',
  defining: true,
  draggable: true,
  attrs: {
    style: { default: 'info' as NoticeStyle },
  },
  parseDOM: [
    {
      tag: 'div.notice-block',
      preserveWhitespace: 'full' as const,
      contentElement: 'div.notice-content',
      getAttrs: (dom) => {
        const element = dom as HTMLElement;
        const classList = element.className;
        let style: NoticeStyle = 'info';
        if (classList.includes('warning')) style = 'warning';
        else if (classList.includes('tip')) style = 'tip';

        return { style };
      },
    },
  ],
  toDOM: (node: ProseMirrorNode) => {
    const style = node.attrs.style as NoticeStyle;

    return ['div', { class: `notice-block ${style}` }, ['div', { class: 'notice-icon' }], ['div', { class: 'notice-content' }, 0]];
  },
  parseMarkdown: {
    match: (node) => node.type === 'containerDirective' && NOTICE_STYLES.includes(node.name as NoticeStyle),
    runner: (state, node, type) => {
      const style = node.name as NoticeStyle;
      state.openNode(type, { style }).next(node.children).closeNode();
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === NOTICE_NODE_NAME,
    runner: (state, node) => {
      const style = node.attrs.style as NoticeStyle;
      state.openNode('containerDirective', undefined, { name: style });
      state.next(node.content);
      state.closeNode();
    },
  },
}));

/**
 * Command to change the style of a notice block the cursor is inside
 */
export const setNoticeStyleCommand = $command('SetNoticeStyle', () => {
  return (style: NoticeStyle = 'info') =>
    (state, dispatch) => {
      const { $from } = state.selection;
      for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        if (node.type.name === NOTICE_NODE_NAME) {
          if (dispatch) {
            const pos = $from.before(depth);
            dispatch(state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, style }));
          }

          return true;
        }
      }

      return false;
    };
});

/**
 * Input rule: typing ":::" followed by a notice type creates a notice block
 */
export const noticeInputRule = $inputRule((ctx) =>
  wrappingInputRule(/^:::(info|warning|tip)\s$/, noticeNode.type(ctx), (match) => ({ style: match[1] as NoticeStyle })),
);

/**
 * Command to wrap selection in a notice block
 */
export const wrapInNoticeCommand = $command('WrapInNotice', (ctx) => {
  return (style: NoticeStyle = 'info') => wrapIn(noticeNode.type(ctx), { style });
});

/**
 * Export all notice plugins as a single array for easy registration
 * Note: NodeView and Tooltip are React components - see ../plugins/noticeNodeView.tsx and ../plugins/noticeTooltip.tsx
 */
export const noticePlugin: MilkdownPlugin[] = [
  remarkDirectivePlugin,
  noticeNode,
  noticeInputRule,
  wrapInNoticeCommand,
  setNoticeStyleCommand,
].flat();
